import { useEffect, useState } from "react";

/* v2.95 · Grounding grid top-down visualization (IEEE 80).
   Renders the substation footprint as a scaled rectangle with a grid
   pattern reflecting the total conductor length L over area A. Users
   drag the small handles to change dimensions. Fault current, clearing
   time, soil resistivity and body weight are edited inline. */

const CANVAS_H = 340;
const CANVAS_W = 720;

export default function GroundingGrid({ onChange }) {
  const [form, setForm] = useState({
    soil_resistivity_ohm_m: 100,
    grid_length_m: 600,               // total buried conductor length
    grid_area_m2: 900,                // 30 × 30 m default
    grid_depth_m: 0.5,
    conductor_diameter_mm: 10,
    fault_current_ka: 20,
    fault_clearing_s: 0.5,
    body_weight_kg: 70,
  });
  // Track side-length derived from area (assume square).
  const [side, setSide] = useState(Math.sqrt(form.grid_area_m2));

  useEffect(() => {
    onChange?.({ ...form, grid_area_m2: Math.round(side * side) });
  }, [form, side, onChange]);

  const patch = (p) => setForm({ ...form, ...p });

  // Layout — reserve 40 px padding, 60 px right column for legend.
  const padX = 30, padY = 30;
  const drawW = CANVAS_W - padX * 2 - 220;
  const drawH = CANVAS_H - padY * 2;
  const scale = Math.min(drawW, drawH) / Math.max(20, side);
  const rectW = side * scale;
  const rectH = side * scale;
  const rectX = padX + (drawW - rectW) / 2;
  const rectY = padY + (drawH - rectH) / 2;

  // Grid mesh count from total length: L ≈ side * (n+1) * 2 (n = mesh count each way)
  const mesh = Math.max(2, Math.min(12,
    Math.round((form.grid_length_m / (2 * side)) - 1)));
  const meshStep = rectW / (mesh + 1);

  return (
    <div className="border border-white/10 bg-[#0A0C10]" data-testid="gnd-grid">
      <div className="flex flex-wrap items-center gap-2 p-2 border-b border-white/10 text-[10px] font-mono text-gray-400">
        <span>top-down view · square grid A = side² · drag corners to resize</span>
      </div>

      <svg viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`} className="w-full block">
        {/* rectangle */}
        <rect x={rectX} y={rectY} width={rectW} height={rectH}
              fill="#00E5FF10" stroke="#00E5FF" strokeWidth="1.5" data-testid="gnd-rect" />

        {/* mesh grid */}
        {Array.from({ length: mesh + 2 }, (_, i) => (
          <g key={i}>
            <line x1={rectX + i * meshStep} x2={rectX + i * meshStep}
                  y1={rectY} y2={rectY + rectH}
                  stroke="#00E5FF66" strokeWidth="0.5" />
            <line y1={rectY + i * meshStep} y2={rectY + i * meshStep}
                  x1={rectX} x2={rectX + rectW}
                  stroke="#00E5FF66" strokeWidth="0.5" />
          </g>
        ))}

        {/* dimension label */}
        <text x={rectX + rectW / 2} y={rectY - 6} textAnchor="middle"
              fill="#00E5FF" fontFamily="monospace" fontSize="11">
          {side.toFixed(1)} m
        </text>
        <text x={rectX - 8} y={rectY + rectH / 2} textAnchor="end"
              fill="#00E5FF" fontFamily="monospace" fontSize="11"
              transform={`rotate(-90 ${rectX - 8} ${rectY + rectH / 2})`}>
          {side.toFixed(1)} m
        </text>

        {/* Corner handles */}
        {[
          { x: rectX + rectW, y: rectY + rectH, dir: "SE" },
        ].map((h) => (
          <rect key={h.dir} x={h.x - 5} y={h.y - 5} width="10" height="10"
                fill="#FFCC00" stroke="#0A0C10" strokeWidth="2"
                className="cursor-nwse-resize"
                data-testid={`gnd-handle-${h.dir}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  const start = { x: e.clientX, y: e.clientY, side };
                  const move = (ev) => {
                    const dx = ev.clientX - start.x;
                    const dy = ev.clientY - start.y;
                    const delta = Math.max(dx, dy) / scale;
                    setSide(Math.max(5, start.side + delta));
                  };
                  const up = () => {
                    window.removeEventListener("mousemove", move);
                    window.removeEventListener("mouseup", up);
                  };
                  window.addEventListener("mousemove", move);
                  window.addEventListener("mouseup", up);
                }} />
        ))}

        {/* Legend / info */}
        <g transform={`translate(${CANVAS_W - 210}, ${padY})`}>
          <rect x={0} y={0} width={200} height={CANVAS_H - padY * 2}
                fill="#12151C" stroke="#ffffff10" strokeWidth="1" />
          <text x={12} y={22} fill="#8792A2" fontFamily="monospace" fontSize="10"
                letterSpacing="1">// grid preview</text>
          {[
            ["side length",       `${side.toFixed(1)} m`],
            ["area A",            `${Math.round(side * side)} m²`],
            ["total conductor L", `${form.grid_length_m} m`],
            ["depth h",           `${form.grid_depth_m} m`],
            ["ø conductor",       `${form.conductor_diameter_mm} mm`],
            ["mesh (each way)",   `${mesh}`],
            ["ρ soil",            `${form.soil_resistivity_ohm_m} Ω·m`],
            ["fault I",           `${form.fault_current_ka} kA`],
            ["fault t",           `${form.fault_clearing_s} s`],
            ["body wt",           `${form.body_weight_kg} kg`],
          ].map(([k, v], i) => (
            <g key={k} transform={`translate(12, ${44 + i * 22})`}>
              <text fill="#8792A2" fontFamily="monospace" fontSize="9">{k}</text>
              <text x={175} y={0} textAnchor="end"
                    fill="#00E5FF" fontFamily="monospace" fontSize="10">{v}</text>
            </g>
          ))}
        </g>
      </svg>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-1 p-2 border-t border-white/10 text-[10px] font-mono">
        {[
          ["side_length_m",           "Side (m)",           1,    (v) => setSide(v)],
          ["grid_length_m",           "Total L (m)",        1,    (v) => patch({ grid_length_m: v })],
          ["grid_depth_m",            "Depth (m)",          0.1,  (v) => patch({ grid_depth_m: v })],
          ["conductor_diameter_mm",   "ø (mm)",             0.5,  (v) => patch({ conductor_diameter_mm: v })],
          ["soil_resistivity_ohm_m",  "ρ soil (Ω·m)",       1,    (v) => patch({ soil_resistivity_ohm_m: v })],
          ["fault_current_ka",        "Fault I (kA)",       0.1,  (v) => patch({ fault_current_ka: v })],
          ["fault_clearing_s",        "Fault t (s)",        0.05, (v) => patch({ fault_clearing_s: v })],
          ["body_weight_kg",          "Body wt (50/70 kg)", 20,   (v) => patch({ body_weight_kg: v })],
        ].map(([key, label, step, setter]) => (
          <label key={key} className="flex flex-col text-gray-500">
            <span>{label}</span>
            <input type="number" step={step}
              value={key === "side_length_m" ? side.toFixed(1) : form[key]}
              onChange={(e) => setter(Number(e.target.value))}
              className="bg-black/40 border border-white/10 px-1 py-0.5 text-[11px] text-gray-200"
              data-testid={`gnd-canvas-${key}`} />
          </label>
        ))}
      </div>
    </div>
  );
}
